import * as Proj from 'ol/proj';
import { getArea, getDistance } from 'ol/sphere';
import WKT from 'ol/format/WKT';
import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import * as Extent from 'ol/extent';
import {fromExtent} from 'ol/geom/Polygon';
import Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';

const { Utils } = require('@/assets/js/scripts/utils/Utils');

export class MapUtils {
    constructor() {
    }

    static reprojectExtent(extent, srcEPSG, destEPSG) {
        var srcCodeEPSG = new Proj.Projection({
			code: srcEPSG
		});
		
		var destCodeEPSG = new Proj.Projection({
			code: destEPSG
		});
		
		var reprojectedExtent = Proj.transformExtent(extent, srcCodeEPSG, destCodeEPSG);
		
		return reprojectedExtent;
    }

	static reprojectCoords(coords, srcEPSG, destEPSG) {
		if (coords == null || coords[0] == null || coords[1] == null) {
			return coords;
		} 
		var reprojectedCoords = Proj.transform(coords, srcEPSG, destEPSG);	
		return reprojectedCoords;
	}

	static reprojectGeom(geom, srcEPSG, destEPSG) {
		var srcCodeEPSG = new Proj.Projection({
			code: srcEPSG
		});
		var destCodeEPSG = new Proj.Projection({
			code: destEPSG
		});
		geom.transform(srcCodeEPSG, destCodeEPSG);
	}


	static reprojectWktGeom(wktGeom, srcEPSG, destEPSG) {
		let geom = MapUtils.wkt2geom(wktGeom);
		MapUtils.reprojectGeom(geom, srcEPSG, destEPSG);

		return geom;
	}

	/**
	 * Restituisce la lunghezza della linea 
	 * nel sistema di riferimento della geometria
	 * 
	 * In questo caso vine restituita la misura geodetica
	 * Se non si vuole la misura geodetica è sufficiente sostituire
	 * il calcolo della misura all'interno del ciclo 
	 * che utilizza il WGS84SPHERE, con la semplice lunghezza 
	 * della geometria:
	 * 		geometry.getLength();
	 * 
	 * @param geometry
	 */
	static getMeasureLine(geometry, mapProjection) {
		var coordinates = geometry.getCoordinates(geometry);
		
		// Misura non geodesica
		//var measurement = geometry.getLength();
		
		// Misura geodetica.
		var measurement = 0;
		for(var i = 0, ii = coordinates.length - 1; i < ii; ++i) {
			var c1 = Proj.transform(coordinates[i], mapProjection, 'EPSG:4326');
	        var c2 = Proj.transform(coordinates[i + 1], mapProjection, 'EPSG:4326');

	        measurement += getDistance(c1, c2);
		}		

		// Approssima il valore di misura in funzione dell'unità da visualizzare: metri - km ..
		var measurementFormatted = measurement > 100 ? (measurement / 1000).toFixed(2) + 'km' : measurement.toFixed(2) + 'm';
		
		return measurementFormatted;
	}

	/**
	 * Restituisce la misura dell'area della geometria poligonale specificata
	 * nel sistema di riferimento della geometria.
	 * 
	 * In questo caso vine restituita la misura geodetica.
	 * Se non si vuole la misura geodetica è sufficiente sostituire
	 * il calcolo della misura che utilizza il WGS84SPHERE, 
	 * con la semplice area del poligono: 
	 * 		geometry.getArea();
	 * 
	 * @param geometry
	 * @param mapProjection
	 * @returns
	 */
	static getMeasurePolygon(geometry, mapProjection) {
		// Misura non geodesica
		//var area = geometry.getArea();
		
		// Misura geodesica dell'area del poligono
		//var geomClone = geometry.clone().transform(mapProjection, 'EPSG:4326');
	    var area = getArea(geometry);
	    
	    var measurementFormatted = area > 10000 ? (area / 1000000).toFixed(2) + ' ' + "km<sup>2</sup>" : area.toFixed(2) + "m<sup>2</sup>";
	    
	    return measurementFormatted;
	}

	static getMeasurePolygonMeter(geometry, mapProjection) {
		var area = getArea(geometry);
		return area;
	}

	/**
	 * Crea Una feature puntuale a partire da una coppia di coordinate
	 * 
	 * @param {*} coordinates 
	 * @param {*} properties 
	 * @param {*} style 
	 * @returns 
	 */
	static createPointFromCoordinates(coordinates, properties, style) {
		let geometry = new Point(coordinates);
		return MapUtils.createFeatureFromGeometry(geometry, properties, style);
	}

	/**
	 * Crea Una geom WKT dalle coordinate
	 * 
	 * @param {*} coordinates 
	 * @param {*} properties 
	 * @param {*} style 
	 * @returns 
	 */
	static createWktPointFromCoordinates(coordinates) {
		let geometry = new Point(coordinates);
		return MapUtils.geom2wkt(geometry);
	}

	/**
	 * Build feature using geometry and its attributes
	 * @param geometry
	 * @param attributes
	 * @returns
	 */
	static createFeatureFromGeometry(geometry, properties, style) {
		// Create empty feature
		var feat = new Feature();
		// Set Geometry to the feature
		feat.setGeometry(geometry);
		
		// Set properties to the feature
		if(!Utils.isNullOrUndefined(properties)) {
			feat.setProperties(properties);
		}
		
		if(!Utils.isNullOrUndefined(style)) {
			feat.setStyle(style);
		}
		
		return feat;
	}

	/**
	 * Crea una feature vettoriale a partire da una geometria WKT
	 * @param {*} wktGeometry 
	 * @param {*} properties 
	 * @param {*} style 
	 * @returns 
	 */
	static createFeatureFromWktGeometry(wktGeometry, properties, style) {
		let geom = MapUtils.wkt2geom(wktGeometry);
		let feat = MapUtils.createFeatureFromGeometry(geom, {}, null);

		return feat;
	}

	/**
	 * Crea una geometria polygon dall'extent
	 * 
	 * @param {Extent} extent 
	 * @returns 
	 */
	static createPolygonFromExtent(extent) {
		return new fromExtent(extent);
	}

	static resizeGeometry(geometry, scaleX, scaleY) {
		if(Utils.isNullOrUndefined(scaleY)) {
			geometry.scale(scaleX, scaleY);
		}
		else {
			geometry.scale(scaleX);
		}
	}

	static isValidGeoemtry(geometry) {
		var isValid = false;
		if (!Utils.isNullOrUndefined(geometry)) {
			isValid = true;
		}
		
		return isValid;
	}

	/**
	 * Da wkt a geometry
	 * @param wkt
	 * @returns geoemtry
	 */
	static wkt2geom(wkt) {
		var wktFormat = new WKT();
		var geom = wktFormat.readGeometry(wkt);
		
		if (!MapUtils.isValidGeoemtry(geom))
			geom = null;
		
		return geom; 
	}

	static geom2wkt(geom) {
		var wkt = null;
		
		if(this.isValidGeoemtry(geom)) {
			var wktFormat = new WKT();
			wkt = wktFormat.writeGeometry(geom);
		}
		
		return wkt;	
	}

	static geoJson2geom(geoJson) {
		let geometry = null;

		if(!Utils.isNullOrUndefined(geoJson)) {
			var geoJsonFormat = new GeoJSON();
			geometry = geoJsonFormat.readGeometry(geoJson);
		}
		return geometry;
	}


	/**
	 *  Converte una lista di feature geojson in features OL
	 */
	 static featureGeoJsonToFeature(geoJsonFeature, sourceEpsg, targetEpsg) {
		var feature = null;
		if(!Utils.isNullOrUndefined(geoJsonFeature)) {
			var geometry = MapUtils.geoJson2geom(geoJsonFeature.geometry);
			
			// Eventuale riproiezione
			if(!Utils.isNullOrUndefined(sourceEpsg) && !Utils.isNullOrUndefined(targetEpsg)) {
				MapUtils.reprojectGeom(geometry, sourceEpsg, targetEpsg);
			}
			
			feature = MapUtils.createFeatureFromGeometry(geometry, geoJsonFeature.properties);
		}	
		
		return feature;
	}


	static addFeaturesToLayer(features, layer, clean) {
		if(!Utils.isNullOrUndefined(layer) && !Utils.isNullOrUndefined(features)) {
			if(clean) {
				this.clearLayer(layer);
			}
			layer.getSource().addFeatures(features);		
		}
	}
  
	static addFeatureToLayer(feature, layer, clean) {
		if(!Utils.isNullOrUndefined(layer) && !Utils.isNullOrUndefined(feature)) {
			if(clean) {
				this.clearLayer(layer);
			}
			layer.getSource().addFeature(feature);	
		}
	}

	static getFeaturesBy(layer, propName, propValue) {
		if(!Utils.isNullOrUndefined(layer) && !Utils.isNullOrUndefined(propName)) {
			let feats = layer.getSource().getFeatures();
			let filteredFeatures = feats.filter(f => {
				let props = f.getProperties();
				if (props[propName] === propValue) {
					return f;
				}
			});

			return filteredFeatures;
		}
	}

	static removeFeatureFromLayer(layer, propName, propVal) {
		if(!Utils.isNullOrUndefined(layer) && !Utils.isNullOrUndefined(propName)) {
			let features = MapUtils.getFeaturesBy(layer, propName, propVal);
			features.forEach(f => {
				layer.getSource().removeFeature(f);	
			});
		}
	}

	static getExtentFromFeaturesGeoJson(featGeoJson) {
		if (Utils.isNullOrUndefined(featGeoJson)) {
			return null;
		}
		if (Utils.isNullOrUndefined(featGeoJson.features) || featGeoJson.features.length == 0) {
			return null;
		}

		let geometries = featGeoJson.features.map(f => MapUtils.geoJson2geom(f.geometry));
		let extent = geometries[0].getExtent().slice(0);

		geometries.forEach(g => Extent.extend(extent, g.getExtent()));
		return extent;
	}

	static zoomToGeometry(mapInstance, geometry) {
		let extent = geometry.getExtent();
		mapInstance.getView().fit( extent, mapInstance.getSize() );
	}

	static zoomToExtent(mapInstance, extent, extraOptions, zoomDelta) {
		let options = {};

		if (!Utils.isNullOrUndefined(extraOptions)) {
			options = extraOptions;
		}

		options.size = mapInstance.getSize();
		mapInstance.getView().fit( extent, options );

		if (!Utils.isNullOrUndefined(zoomDelta) && (zoomDelta > 0 || zoomDelta < 0)) {
			var currentZoom = mapInstance.getView().getZoom();
			mapInstance.getView().setZoom( currentZoom - zoomDelta );
		}
	}

	static clearLayer(layer) {
		if(!Utils.isNullOrUndefined(layer)) {
			layer.getSource().clear();
		}
	}

	static setLayerVisible(layer, visibility) {
		if(!Utils.isNullOrUndefined(layer)) {
			layer.setVisible(visibility);
		}
	}

	static convertMillimitersToMapUnit(mm, scale, mapUnit) {
        // Recupera i millimitri per unita' di mappa
		var mmPerMapUnit = MapUtils.getMillimterPerMapUnit(mapUnit);
 
		var mapUnits = mm / mmPerMapUnit;
		mapUnits = mapUnits * scale;
		
		return mapUnits;
    }

	/**
	 *  Restituisce i metri in una unita' di mappa
	 */
	static getMeterPerMapUnit = function(mapUnit) {
		var mpu = Proj.METERS_PER_UNIT[mapUnit];
		
		return mpu;
	}
	
	/**
	 *  Restituisce i millimetri in una unita' di mappa
	 */
	static getMillimterPerMapUnit = function(mapUnit) {
		return MapUtils.getMeterPerMapUnit(mapUnit) * 1000;
	}

	/**
	 *  Restituisce l'opacity del layer
	 */
	static getLayerOpacity(layer) {
		var opacity = null;
		
		if(!Utils.isNullOrUndefined(layer)) {
			opacity = layer.getOpacity();		
		}
		
		return opacity;
	}

	/**
	 * Restituisce lo styles del layer se c'è
	 */
	static getLayerStyles(layer) {
		var styles = null;
		
		if(!Utils.isNullOrUndefined(layer)) {
			var source = layer.getSource();
			if (!Utils.isNullOrUndefined(source)) {
				var params = source.getParams()
				
				if (!Utils.isNullOrUndefined(params)) {
					styles = params.STYLES;
				}
				
			}
		}
		
		return styles;
	}

	/**
	 * Restituisce il param SLD del layer se c'è
	 */
	 static getLayerSLD(layer) {
		var sld = null;
		
		if(!Utils.isNullOrUndefined(layer)) {
			var source = layer.getSource();
			if (!Utils.isNullOrUndefined(source)) {
				var params = source.getParams()
				
				if (!Utils.isNullOrUndefined(params)) {
					sld = params.SLD;
				}
				
			}
		}
		
		return sld;
	}

	static getLayerViewParams(layer) {
		var viewparams = null;
		
		if(!Utils.isNullOrUndefined(layer)) {
			var source = layer.getSource();
			if (!Utils.isNullOrUndefined(source)) {
				var params = source.getParams()
				
				if (!Utils.isNullOrUndefined(params)) {
					viewparams = params.viewparams;
				}
				
			}
		}
		
		return viewparams;
	}

	/**
	 *  Restituisce l'a url del layer wms sepcificato
	 */
	static getWmsLayerUrlParam(layer) {
		var url = null;
		
		if(!Utils.isNullOrUndefined(layer)) {
			var source = layer.getSource();
			var urlsList = source.getUrls();
			
			if(urlsList.length > 0) {
				url = urlsList[0];
			}
		}
		
		return url;
	}

	/**
	 *  Restituisce la stringa (lista) di layer caricati nell'istanza del layer OL sepcificata
	 *  Rappresenta la lista dei nomi dei layer del WMS caricati richiesti nella chiamata WMS
	 */
	static getWmsLayersListParam(layer) {
		var layersList = [];
		
		if(!Utils.isNullOrUndefined(layer)) {
			var source = layer.getSource();
			if(typeof source.getParams === "function" && !Utils.isEmptyObject(source.getParams())) {
				var layersListStr = source.getParams().LAYERS;
				layersList  = layersListStr.split(",");
			}			
		}
		
		return layersList;
	}

	static getFeaturePropertiesForTooltip(feature) {
		if(!Utils.isNullOrUndefined(feature)) {
			let properties = feature.getProperties();
			return properties;
		}
		return null;
	}
	
}